import React, { useState, useEffect, useContext } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiPlus, FiTrash2, FiSearch } from 'react-icons/fi';
import { CRMContext } from '../context/CRMContext';

const ModalWrapper = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export const LeadModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const { leads } = useContext(CRMContext);
  const [formData, setFormData] = useState({
    name: '', company: '', 
    phones: [{ value: '', label: 'main' }],
    emails: [{ value: '', label: 'main' }],
    telegram: '', status: 'New', source: 'Website',
    referrerType: 'manual', referrerManual: '', referrerLeadId: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        company: initialData.company || '',
        phones: initialData.phones?.length ? initialData.phones : [{ value: '', label: 'main' }],
        emails: initialData.emails?.length ? initialData.emails : [{ value: '', label: 'main' }],
        telegram: initialData.telegram || '',
        status: initialData.status || 'New',
        referrerType: initialData.referrerType || 'manual',
        referrerManual: initialData.referrerManual || '',
        referrerLeadId: initialData.referrerLeadId || '',
        source: initialData.source || 'Website'
      });
    } else {
      setFormData({
        name: '', company: '', 
        phones: [{ value: '', label: 'main' }],
        emails: [{ value: '', label: 'main' }],
        telegram: '', status: 'New', source: 'Website',
        referrerType: 'manual', referrerManual: '', referrerLeadId: ''
      });
    }
  }, [initialData, isOpen]);

  const addField = (type) => {
    setFormData(prev => ({ ...prev, [type]: [...prev[type], { value: '', label: 'main' }] }));
  };

  const removeField = (type, index) => {
    if (formData[type].length <= 1) return;
    setFormData(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== index) }));
  };

  const updateField = (type, index, key, val) => {
    const list = [...formData[type]];
    list[index][key] = val;
    setFormData({ ...formData, [type]: list });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submission = {
      ...formData,
      phones: formData.phones.filter(p => p.value.trim() !== ''),
      emails: formData.emails.filter(e => e.value.trim() !== '')
    };
    onSubmit(submission);
    onClose();
  };

  const phoneLabels = [
    { value: 'main', label: 'Principal' },
    { value: 'office', label: 'Oficina' },
    { value: 'personal', label: 'Personal' },
    { value: 'assistant', label: 'Asistente' },
    { value: 'other', label: 'Otro' }
  ];
  const emailLabels = [
    { value: 'main', label: 'Principal' },
    { value: 'work', label: 'Trabajo' },
    { value: 'personal', label: 'Personal' },
    { value: 'other', label: 'Otro' }
  ];
  const sources = [
    { value: 'Website', label: 'Sitio Web' },
    { value: 'GMB', label: 'GMB' },
    { value: 'Facebook', label: 'Facebook' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'Recommended', label: 'Recomendado' },
    { value: 'Other', label: 'Otro' }
  ];

  const statusOptions = [
    { value: 'New', label: 'Nuevo' },
    { value: 'Contacted', label: 'Contactado' },
    { value: 'Qualified', label: 'Calificado' },
    { value: 'Lost', label: 'Perdido' },
    { value: 'Won', label: 'Ganado' }
  ];

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title={initialData?.id ? "Editar Lead" : "Agregar Nuevo Lead"}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Completo *</label>
            <input required type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Empresa</label>
            <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} />
          </div>
        </div>

        {/* Dynamic Phones */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Números de Teléfono</label>
          <div className="space-y-2">
            {formData.phones.map((phone, idx) => (
              <div key={idx} className="flex gap-2">
                <select className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50" value={phone.label} onChange={e => updateField('phones', idx, 'label', e.target.value)}>
                  {phoneLabels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                <input type="tel" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Número" value={phone.value} onChange={e => updateField('phones', idx, 'value', e.target.value)} />
                {formData.phones.length > 1 && (
                  <button type="button" onClick={() => removeField('phones', idx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><SafeIcon icon={FiTrash2} /></button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => addField('phones')} className="mt-2 text-sm text-blue-600 font-medium flex items-center gap-1 hover:underline">
            <SafeIcon icon={FiPlus} /> Agregar teléfono
          </button>
        </div>

        {/* Dynamic Emails */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Correos Electrónicos</label>
          <div className="space-y-2">
            {formData.emails.map((email, idx) => (
              <div key={idx} className="flex gap-2">
                <select className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50" value={email.label} onChange={e => updateField('emails', idx, 'label', e.target.value)}>
                  {emailLabels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                <input type="email" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Correo" value={email.value} onChange={e => updateField('emails', idx, 'value', e.target.value)} />
                {formData.emails.length > 1 && (
                  <button type="button" onClick={() => removeField('emails', idx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><SafeIcon icon={FiTrash2} /></button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => addField('emails')} className="mt-2 text-sm text-blue-600 font-medium flex items-center gap-1 hover:underline">
            <SafeIcon icon={FiPlus} /> Agregar correo
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Estado</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
              {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Origen del Lead</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.source} onChange={e => setFormData({ ...formData, source: e.target.value })}>
              {sources.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {formData.source === 'Recommended' && (
          <div className="p-4 bg-blue-50 rounded-lg space-y-3">
            <label className="block text-xs font-bold text-blue-700 uppercase">Referido por</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="refType" checked={formData.referrerType === 'manual'} onChange={() => setFormData({ ...formData, referrerType: 'manual' })} /> Nombre Manual
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="refType" checked={formData.referrerType === 'linked'} onChange={() => setFormData({ ...formData, referrerType: 'linked' })} /> Lead Existente
              </label>
            </div>
            {formData.referrerType === 'manual' ? (
              <input type="text" placeholder="Nombre del referente" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.referrerManual} onChange={e => setFormData({ ...formData, referrerManual: e.target.value })} />
            ) : (
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.referrerLeadId} onChange={e => setFormData({ ...formData, referrerLeadId: e.target.value })}>
                <option value="">Seleccionar lead existente...</option>
                {leads.filter(l => l.id !== initialData?.id).map(l => (
                  <option key={l.id} value={l.id}>{l.name} ({l.company})</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Telegram</label>
          <input type="text" placeholder="@usuario" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.telegram} onChange={e => setFormData({ ...formData, telegram: e.target.value })} />
        </div>

        <div className="pt-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
          <button type="submit" className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium">Guardar Lead</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export const EventModal = ({ isOpen, onClose, onSubmit, leads, initialData = null, fixedLeadId = null }) => {
  const [formData, setFormData] = useState({
    type: 'task', title: '', description: '', leadId: fixedLeadId || '',
    date: '', time: '09:00', status: 'pending'
  });

  useEffect(() => {
    if (initialData) {
      const d = new Date(initialData.date);
      setFormData({ ...initialData, date: d.toISOString().split('T')[0], time: d.toTimeString().slice(0, 5) });
    } else {
      setFormData({
        type: 'task', title: '', description: '', leadId: fixedLeadId || (leads.length > 0 ? leads[0].id : ''),
        date: new Date().toISOString().split('T')[0], time: '09:00', status: 'pending'
      });
    }
  }, [initialData, isOpen, fixedLeadId, leads]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const dateTime = new Date(`${formData.date}T${formData.time}`).toISOString();
    const submitData = { ...formData, date: dateTime };
    delete submitData.time;
    onSubmit(submitData);
    onClose();
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title={initialData ? "Editar Evento" : "Crear Evento"}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
              <option value="task">Tarea</option>
              <option value="appointment">Cita</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
              <option value="pending">Pendiente</option>
              <option value="completed">Completado</option>
            </select>
          </div>
        </div>

        {!fixedLeadId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lead Relacionado</label>
            <select required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.leadId} onChange={e => setFormData({ ...formData, leadId: e.target.value })}>
              <option value="">Seleccionar un lead...</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.name} ({l.company})</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
          <input required type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
            <input required type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hora *</label>
            <input required type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}></textarea>
        </div>

        <div className="pt-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
          <button type="submit" className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium">Guardar Evento</button>
        </div>
      </form>
    </ModalWrapper>
  );
};